import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import {
  ApplicationStatus,
  EmploymentStatus,
  EmploymentSector,
  DesignationLevel,
  PrimaryIncomeSource,
  PrimaryWealthSource
} from '../../enum/enums';
import {
  CreateLegalEntityRequest,
  LegalEntityResponse,
  LegalEntityGetResponse
} from '../../interfaces/legal-entity.interfaces';
import {
  CreateControllingPersonRequest,
  ControllingPersonResponse,
  ControllingPersonGetResponse,
  ControllingPersonListResponse,
  AvailablePersonsResponse
} from '../../interfaces/controlling-persons.interfaces';
import {
  CreateDirectorExecutiveRequest,
  DirectorExecutiveResponse,
  DirectorExecutiveListResponse,
  AvailableDirectorsExecutivesResponse,
  DirectorExecutiveGetResponse
} from '../../interfaces/directors-executives.interfaces';
import {
  AuthorizedPersonListResponse,
  AuthorizedPersonResponse,
  CreateAuthorizedPersonRequest,
  AuthorizedPersonGetResponse,
  AvailablePersonsResponse as AuthorizedAvailablePersonsResponse
} from '../../interfaces/authorized-persons.interfaces';
import {
  CreateContactAddressRequest,
  ContactAddressGetResponse,
  ContactAddressResponse
} from '../../interfaces/contact-address.interfaces';
import { BankCustodianGetResponse, BankCustodianResponse, CreateBankCustodianRequest } from '../../interfaces/bank-custodian.interfaces';
import { AvailablePepResponse, PepListResponse, PepGetResponse, CreatePepRequest } from '../../interfaces/pep-declaration.interfaces';
import { lookupResponse } from '../../interfaces/countries-interfaces';
import { DocumentRow, PersonDocument } from '../../interfaces/documents.interface';
import { LegalEntityKycPersonsResponse } from '../../interfaces/legal-entity-kyc-persons.interfaces';

// Employment Details interfaces
export interface ControllingPersonEmploymentDetailsRequest {
  controllingPersonId: number;
  employmentStatusId: EmploymentStatus;
  employerName?: string;
  employerAddress?: string;
  employmentSectorId?: EmploymentSector;
  designationLevelId?: DesignationLevel;
  jobTitle?: string;
  employmentStartDate?: string;
  employmentEndDate?: string;
  annualIncome?: number;
  primaryIncomeSourceId?: PrimaryIncomeSource;
  otherPrimaryIncomeSource?: string;
  primaryWealthSourceId?: PrimaryWealthSource;
  otherPrimaryWealthSource?: string;
  netWorth?: number;
  isPoliticallyExposedPerson?: boolean;
  politicallyExposedPersonDetails?: string;
}

export interface ControllingPersonEmploymentDetailsResponse {
  controllingPersonId: number;
  employmentStatusId: EmploymentStatus;
  employerName?: string;
  employerAddress?: string;
  employmentSectorId?: EmploymentSector;
  designationLevelId?: DesignationLevel;
  jobTitle?: string;
  employmentStartDate?: string;
  employmentEndDate?: string;
  annualIncome?: number;
  primaryIncomeSourceId?: PrimaryIncomeSource;
  otherPrimaryIncomeSource?: string;
  primaryWealthSourceId?: PrimaryWealthSource;
  otherPrimaryWealthSource?: string;
  netWorth?: number;
  isPoliticallyExposedPerson?: boolean;
  politicallyExposedPersonDetails?: string;
  createdAt: string;
  updatedAt: string;
}

// General KYC interfaces
export interface ControllingPersonGeneralKyc {
  personId: number;
  isBoardMemberOrSeniorExecutive: number;
  listedCompanyName: string;
  isBeneficialOwnerWithSuknaCapital: number;
  beneficialOwnerRelationshipDetails: string;
  isCloseAssociationWithBoardMember: number;
  closeAssociationCompanyName: string;
  isEntrustedWithProminentPublicFunction: number;
  prominentPublicFunctionDetails: string;
  hasRelationshipWithPep: number;
  relationshipWithPepDetails: string;
  hasBeenRefusedBusinessRelationship: number;
  refusalDetails: string;
  hasOtherRelevantFinancialInformation: number;
  otherRelevantFinancialInformationDetails: string;
  additionalInvestmentInformation: string;
}
import { CreateShareholderLegalEntityRequest, ShareholderLegalEntityGetResponse, ShareholderLegalEntityListResponse, ShareholderLegalEntityResponse } from '../../interfaces/shareholder-legal-entity.interfaces';

export interface GetApplicationOptions {
  /** When true, bypasses the in-memory cache and fetches from the API. */
  forceRefresh?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class OnboardingService {

  private countriesCache: lookupResponse | null = null;
  /** Shared in-flight/completed result for GetCurrentApplications (avoids duplicate calls per step). */
  private currentApplicationsRequest$: Observable<any> | null = null;
  private _applicationStatus$ = new BehaviorSubject<ApplicationStatus | null>(null);
  applicationStatus$ = this._applicationStatus$.asObservable();

  /**
   * Global read-only/view-only flag for the whole onboarding application.
   * Driven by the `?view=1` route param (set in OnboardingLayout) so any step
   * can render itself read-only from a single source of truth.
   */
  private _isViewOnly$ = new BehaviorSubject<boolean>(false);
  isViewOnly$ = this._isViewOnly$.asObservable();

  private baseUrl: string = '';

  constructor(
    private _http: HttpClient
  ) {
    this.baseUrl = `${environment.baseUrl}/lms/api/onboarding/applications`;
  }

  setApplicationStatus(status: ApplicationStatus | null): void {
    this._applicationStatus$.next(status);
  }

  setViewOnly(isViewOnly: boolean): void {
    this._isViewOnly$.next(isViewOnly);
  }

  /** Synchronous accessor for the current global view-only state. */
  get isViewOnly(): boolean {
    return this._isViewOnly$.value;
  }

  // create Application
  createApplication(crNumber: number | null | string = null): Observable<any> {
    return this._http.post(`${this.baseUrl}`, { crNumber })
      .pipe(
        tap(() => this.clearCurrentApplicationsCache()),
        catchError(this.handleError)
      );
  }

  /** Clears the cached GetCurrentApplications response (e.g. when leaving onboarding). */
  clearCurrentApplicationsCache(): void {
    this.currentApplicationsRequest$ = null;
  }

  // Get Application
  getApplication(options?: GetApplicationOptions): Observable<any> {
    if (options?.forceRefresh) {
      this.clearCurrentApplicationsCache();
    }

    if (!this.currentApplicationsRequest$) {
      this.currentApplicationsRequest$ = this._http
        .get<any[]>(`${this.baseUrl}/GetCurrentApplications`)
        .pipe(
          catchError((err) => {
            this.clearCurrentApplicationsCache();
            return this.handleError(err);
          }),
          shareReplay({ bufferSize: 1, refCount: false })
        );
    }

    return this.currentApplicationsRequest$;
  }

  // Save Legal Entity step
  saveLegalEntity(payload: CreateLegalEntityRequest, applicationId: number): Observable<LegalEntityResponse> {
    return this._http.post<LegalEntityResponse>(`${this.baseUrl}/${applicationId}/legal-entity`, payload)
      .pipe(catchError(this.handleError));
  }

  // Get Legal Entity
  getLegalEntity(applicationId: number): Observable<LegalEntityGetResponse> {
    return this._http.get<LegalEntityGetResponse>(`${this.baseUrl}/${applicationId}/legal-entity`)
      .pipe(catchError(this.handleError));
  }

  // Get Validator
  getValidatorErrors(applicationId: number): Observable<any> {
    return this._http.get<any>(`${this.baseUrl}/${applicationId}/validator`)
      .pipe(catchError(this.handleError));
  }

  // Submit onboarding
  submitOnboarding(id: number): Observable<any> {
    return this._http.post<any>(`${this.baseUrl}/${id}/submit`, {})
      .pipe(
        tap(() => this.clearCurrentApplicationsCache()),
        catchError(this.handleError)
      );
  }


  // Update Legal Entity
  updateLegalEntity(applicationId: number, legalEntity: CreateLegalEntityRequest): Observable<LegalEntityResponse> {
    return this._http.put<LegalEntityResponse>(`${this.baseUrl}/${applicationId}/legal-entity`, legalEntity)
      .pipe(catchError(this.handleError));
  }

  // Get Contact Address
  getContactAddress(applicationId: number): Observable<ContactAddressGetResponse> {
    return this._http.get<ContactAddressGetResponse>(`${this.baseUrl}/${applicationId}/legal-entity/contact`)
      .pipe(catchError(this.handleError));
  }

  // Create Contact Address
  createContactAddress(applicationId: number, contactAddress: CreateContactAddressRequest): Observable<ContactAddressResponse> {
    return this._http.post<ContactAddressResponse>(`${this.baseUrl}/${applicationId}/legal-entity/contact`, contactAddress)
      .pipe(catchError(this.handleError));
  }

  // Update Contact Address
  updateContactAddress(applicationId: number, contactAddress: CreateContactAddressRequest): Observable<ContactAddressResponse> {
    return this._http.put<ContactAddressResponse>(`${this.baseUrl}/${applicationId}/legal-entity/contact`, contactAddress)
      .pipe(catchError(this.handleError));
  }

  // Get Director Executive 
  getDirectorExecutive(applicationId: number): Observable<DirectorExecutiveListResponse> {
    return this._http.get<DirectorExecutiveListResponse>(`${this.baseUrl}/${applicationId}/directors-executives`)
      .pipe(catchError(this.handleError));
  }

  // Save Director Executive
  saveDirectorExecutive(applicationId: number, directorExecutive: CreateDirectorExecutiveRequest): Observable<DirectorExecutiveResponse> {
    return this._http.post<DirectorExecutiveResponse>(`${this.baseUrl}/${applicationId}/directors-executives`, directorExecutive)
      .pipe(catchError(this.handleError));
  }

  // Update Director Executive
  updateDirectorExecutive(applicationId: number, directorExecutive: CreateDirectorExecutiveRequest, associationId: number): Observable<DirectorExecutiveResponse> {
    return this._http.put<DirectorExecutiveResponse>(`${this.baseUrl}/${applicationId}/directors-executives/${associationId}`, directorExecutive)
      .pipe(catchError(this.handleError));
  }


  // Get available Directors Executive 
  getDirectorExecutiveAvailable(applicationId: number): Observable<AvailableDirectorsExecutivesResponse> {
    return this._http.get<AvailableDirectorsExecutivesResponse>(`${this.baseUrl}/${applicationId}/directors-executives/available`)
      .pipe(catchError(this.handleError));
  }

  // Get Director Executive by ID
  getDirectorExecutiveById(applicationId: number, associationId: number): Observable<DirectorExecutiveGetResponse> {
    return this._http.get<DirectorExecutiveGetResponse>(`${this.baseUrl}/${applicationId}/directors-executives/${associationId}`)
      .pipe(catchError(this.handleError));
  }

  // Delete Director Executive
  deleteDirectorExecutive(applicationId: number, associationId: number): Observable<any> {
    return this._http.delete<any>(`${this.baseUrl}/${applicationId}/directors-executives/${associationId}`)
      .pipe(catchError(this.handleError));
  }

  // Controlling Person
  getControllingPerson(applicationId: number): Observable<ControllingPersonListResponse> {
    return this._http.get<ControllingPersonListResponse>(`${this.baseUrl}/${applicationId}/controlling-persons`)
      .pipe(catchError(this.handleError));
  }

  // Get Controlling Person Ownership Above 25
  getControllingPersonOwnershipAbove25(applicationId: number): Observable<ControllingPersonListResponse> {
    return this._http.get<ControllingPersonListResponse>(`${this.baseUrl}/${applicationId}/controlling-persons/ownership-above-25`)
      .pipe(catchError(this.handleError));
  }

  // Save Controlling Person
  saveControllingPerson(applicationId: number, controllingPerson: CreateControllingPersonRequest): Observable<ControllingPersonResponse> {
    return this._http.post<ControllingPersonResponse>(`${this.baseUrl}/${applicationId}/controlling-persons`, controllingPerson)
      .pipe(catchError(this.handleError));
  }

  // Get available Controlling Persons 
  getControllingPersonsAvailable(applicationId: number, parentShareholderLegalEntityId?: number | null): Observable<AvailablePersonsResponse> {
    let url = `${this.baseUrl}/${applicationId}/controlling-persons/available`;
    if (parentShareholderLegalEntityId) {
      url += `?shareholderLegalEntityId=${parentShareholderLegalEntityId}`;
    }
    return this._http.get<AvailablePersonsResponse>(url)
      .pipe(catchError(this.handleError));
  }

  // Update  Controlling Persons
  updateControllingPersons(applicationId: number, controllingPerson: CreateControllingPersonRequest, associationId: number): Observable<ControllingPersonResponse> {
    return this._http.put<ControllingPersonResponse>(`${this.baseUrl}/${applicationId}/controlling-persons/${associationId}`, controllingPerson)
      .pipe(catchError(this.handleError));
  }

  // Get Controlling Person by ID
  getControllingPersonById(applicationId: number, associationId: number): Observable<ControllingPersonGetResponse> {
    return this._http.get<ControllingPersonGetResponse>(`${this.baseUrl}/${applicationId}/controlling-persons/${associationId}`)
      .pipe(catchError(this.handleError));
  }

  // Delete Controlling Person
  deleteControllingPerson(applicationId: number, associationId: number): Observable<any> {
    return this._http.delete<any>(`${this.baseUrl}/${applicationId}/controlling-persons/${associationId}`)
      .pipe(catchError(this.handleError));
  }

  // Get Controlling Person KYC Information
  getControllingPersonKyc(applicationId: number, personId: number): Observable<any> {
    return this._http.get<any>(`${this.baseUrl}/${applicationId}/persons/${personId}/kyc`)
      .pipe(catchError(this.handleError));
  }

  // Create Controlling Person KYC Information
  createControllingPersonKyc(applicationId: number, personId: number, kycData: any): Observable<any> {
    return this._http.post<any>(`${this.baseUrl}/${applicationId}/persons/${personId}/kyc`, kycData)
      .pipe(catchError(this.handleError));
  }

  // Update Controlling Person KYC Information
  updateControllingPersonKyc(applicationId: number, personId: number, kycData: any): Observable<any> {
    return this._http.put<any>(`${this.baseUrl}/${applicationId}/persons/${personId}/kyc`, kycData)
      .pipe(catchError(this.handleError));
  }

  // Get Controlling Person KYC Employment
  getControllingPersonEmploymentKyc(applicationId: number, personId: number): Observable<any> {
    return this._http.get<any>(`${this.baseUrl}/${applicationId}/persons/${personId}/kyc/employment-wealth`)
      .pipe(catchError(this.handleError));
  }

  // Create Controlling Person KYC Employment
  createControllingPersonEmploymentKyc(applicationId: number, personId: number, kycData: any): Observable<any> {
    return this._http.post<any>(`${this.baseUrl}/${applicationId}/persons/${personId}/kyc/employment-wealth`, kycData)
      .pipe(catchError(this.handleError));
  }

  // Update Controlling Person KYC Employment
  updateControllingPersonEmploymentKyc(applicationId: number, personId: number, kycData: any): Observable<any> {
    return this._http.put<any>(`${this.baseUrl}/${applicationId}/persons/${personId}/kyc/employment-wealth`, kycData)
      .pipe(catchError(this.handleError));
  }

  // Get Controlling Person KYC General
  getControllingPersonGeneralKyc(applicationId: number, personId: number): Observable<{ data: ControllingPersonGeneralKyc }> {
    return this._http.get<{ data: ControllingPersonGeneralKyc }>(
      `${this.baseUrl}/${applicationId}/persons/${personId}/kyc/general-information`
    ).pipe(catchError(this.handleError));
  }

  // Create Controlling Person KYC General
  createControllingPersonGeneralKyc(
    applicationId: number,
    personId: number,
    kycData: ControllingPersonGeneralKyc
  ): Observable<{ message: string; data: ControllingPersonGeneralKyc }> {
    return this._http.post<{ message: string; data: ControllingPersonGeneralKyc }>(
      `${this.baseUrl}/${applicationId}/persons/${personId}/kyc/general-information`,
      kycData
    ).pipe(catchError(this.handleError));
  }

  // Update Controlling Person KYC General
  updateControllingPersonGeneralKyc(
    applicationId: number,
    personId: number,
    kycData: ControllingPersonGeneralKyc
  ): Observable<{ message: string; data: ControllingPersonGeneralKyc }> {
    return this._http.put<{ message: string; data: ControllingPersonGeneralKyc }>(
      `${this.baseUrl}/${applicationId}/persons/${personId}/kyc/general-information`,
      kycData
    ).pipe(catchError(this.handleError));
  }

  // Get Shareholder Legal Entities (list)
  getShareholderLegalEntities(applicationId: number): Observable<ShareholderLegalEntityListResponse> {
    return this._http
      .get<ShareholderLegalEntityListResponse>(`${this.baseUrl}/${applicationId}/shareholder-legal-entities`)
      .pipe(catchError(this.handleError));
  }

  // Get Legal Entities tree
  getLegalEntitiesTree(applicationId: number): Observable<ShareholderLegalEntityGetResponse> {
    return this._http
      .get<ShareholderLegalEntityGetResponse>(`${this.baseUrl}/${applicationId}/legal-entity/tree`)
      .pipe(catchError(this.handleError));
  }

  // Create Shareholder Legal Entity
  createShareholderLegalEntity(applicationId: number, shareholderLegalEntity: CreateShareholderLegalEntityRequest): Observable<ShareholderLegalEntityResponse> {
    return this._http
      .post<ShareholderLegalEntityResponse>(`${this.baseUrl}/${applicationId}/shareholder-legal-entities`, shareholderLegalEntity)
      .pipe(catchError(this.handleError));
  }

  // Update Shareholder Legal Entity (by associationId)
  updateShareholderLegalEntity(
    applicationId: number,
    id: number,
    shareholderLegalEntity: CreateShareholderLegalEntityRequest
  ): Observable<ShareholderLegalEntityResponse> {
    return this._http
      .put<ShareholderLegalEntityResponse>(
        `${this.baseUrl}/${applicationId}/shareholder-legal-entities/${id}`,
        shareholderLegalEntity
      )
      .pipe(catchError(this.handleError));
  }

  // Get Shareholder Legal Entity by ID
  getShareholderLegalEntityById(applicationId: number, id: number): Observable<ShareholderLegalEntityResponse> {
    return this._http
      .get<ShareholderLegalEntityResponse>(`${this.baseUrl}/${applicationId}/shareholder-legal-entities/${id}`)
      .pipe(catchError(this.handleError));
  }

  // Remove Shareholder Legal Entity by ID
  removeShareholderLegalEntityById(applicationId: number, id: number): Observable<ShareholderLegalEntityResponse> {
    return this._http
      .delete<ShareholderLegalEntityResponse>(`${this.baseUrl}/${applicationId}/shareholder-legal-entities/${id}`)
      .pipe(catchError(this.handleError));
  }
  // Remove Shareholder Legal Entity by ID
  removeControllingPersonById(applicationId: number, id: number): Observable<any> {
    return this._http
      .delete<any>(`${this.baseUrl}/${applicationId}/controlling-persons/${id}`)
      .pipe(catchError(this.handleError));
  }

  // Create Shareholder Controlling Person
  createShareholderControllingPerson(applicationId: number, shareholderLegalEntityId: number, shareholderControllingPerson: CreateControllingPersonRequest): Observable<ControllingPersonResponse> {
    return this._http
      .post<ControllingPersonResponse>(`${this.baseUrl}/${applicationId}/shareholder-legal-entities/${shareholderLegalEntityId}/controlling-persons`, shareholderControllingPerson)
      .pipe(catchError(this.handleError));
  }

  // Get Shareholder Controlling Persons (list)
  getShareholderControllingPersons(applicationId: number, shareholderLegalEntityId: number): Observable<ControllingPersonListResponse> {
    return this._http
      .get<ControllingPersonListResponse>(`${this.baseUrl}/${applicationId}/shareholder-legal-entities/${shareholderLegalEntityId}/controlling-persons`)
      .pipe(catchError(this.handleError));
  }

  //Get Shareholder Controlling Person by ID
  getShareholderControllingPersonById(applicationId: number, shareholderLegalEntityId: number, associationId: number): Observable<ControllingPersonGetResponse> {
    return this._http
      .get<ControllingPersonGetResponse>(`${this.baseUrl}/${applicationId}/shareholder-legal-entities/${shareholderLegalEntityId}/controlling-persons/${associationId}`)
      .pipe(catchError(this.handleError));
  }

  // Update Shareholder Controlling Person (by associationId)
  updateShareholderControllingPerson(
    applicationId: number,
    shareholderLegalEntityId: number,
    associationId: number,
    shareholderControllingPerson: CreateControllingPersonRequest
  ): Observable<ControllingPersonResponse> {
    return this._http
      .put<ControllingPersonResponse>(
        `${this.baseUrl}/${applicationId}/shareholder-legal-entities/${shareholderLegalEntityId}/controlling-persons/${associationId}`,
        shareholderControllingPerson
      )
      .pipe(catchError(this.handleError));
  }

  // Delete Shareholder Controlling Person (by associationId)
  deleteShareholderControllingPerson(
    applicationId: number,
    shareholderLegalEntityId: number,
    associationId: number
  ): Observable<ControllingPersonResponse> {
    return this._http
      .delete<ControllingPersonResponse>(
        `${this.baseUrl}/${applicationId}/shareholder-legal-entities/${shareholderLegalEntityId}/controlling-persons/${associationId}`
      )
      .pipe(catchError(this.handleError));
  }


  // Get Authorized Persons (list)
  getAuthorizedPersons(applicationId: number): Observable<AuthorizedPersonListResponse> {
    return this._http
      .get<AuthorizedPersonListResponse>(`${this.baseUrl}/${applicationId}/authorized-persons`)
      .pipe(catchError(this.handleError));
  }

  // Save / Add Authorized Person
  saveAuthorizedPerson(applicationId: number, authorizedPerson: CreateAuthorizedPersonRequest): Observable<AuthorizedPersonResponse> {
    return this._http
      .post<AuthorizedPersonResponse>(`${this.baseUrl}/${applicationId}/authorized-persons`, authorizedPerson)
      .pipe(catchError(this.handleError));
  }

  // Get Available Authorized Persons
  getAuthorizedPersonsAvailable(applicationId: number): Observable<AuthorizedAvailablePersonsResponse> {
    return this._http
      .get<AuthorizedAvailablePersonsResponse>(`${this.baseUrl}/${applicationId}/authorized-persons/available`)
      .pipe(catchError(this.handleError));
  }

  // Get Authorized Person by ID
  getAuthorizedPersonById(applicationId: number, associationId: number): Observable<AuthorizedPersonGetResponse> {
    return this._http
      .get<AuthorizedPersonGetResponse>(`${this.baseUrl}/${applicationId}/authorized-persons/${associationId}`)
      .pipe(catchError(this.handleError));
  }

  // Update Authorized Person (by associationId)
  updateAuthorizedPerson(
    applicationId: number,
    associationId: number,
    authorizedPerson: CreateAuthorizedPersonRequest
  ): Observable<AuthorizedPersonResponse> {
    return this._http
      .put<AuthorizedPersonResponse>(
        `${this.baseUrl}/${applicationId}/authorized-persons/${associationId}`,
        authorizedPerson
      )
      .pipe(catchError(this.handleError));
  }

  // Delete Authorized Person
  deleteAuthorizedPerson(applicationId: number, associationId: number): Observable<any> {
    return this._http
      .delete<any>(`${this.baseUrl}/${applicationId}/authorized-persons/${associationId}`)
      .pipe(catchError(this.handleError));
  }

  // Create Bank Custodian
  createBankCustodian(applicationId: number, bankCustodian: CreateBankCustodianRequest): Observable<BankCustodianResponse> {
    return this._http.post<BankCustodianResponse>(`${this.baseUrl}/${applicationId}/legal-entity/bank-custodian`, bankCustodian)
      .pipe(catchError(this.handleError));
  }

  // Get Bank Custodian
  getBankCustodian(applicationId: number): Observable<BankCustodianGetResponse> {
    return this._http.get<BankCustodianGetResponse>(`${this.baseUrl}/${applicationId}/legal-entity/bank-custodian`)
      .pipe(catchError(this.handleError));
  }

  // Update Bank Custodian
  updateBankCustodian(applicationId: number, bankCustodian: CreateBankCustodianRequest): Observable<BankCustodianResponse> {
    return this._http.put<BankCustodianResponse>(`${this.baseUrl}/${applicationId}/legal-entity/bank-custodian`, bankCustodian)
      .pipe(catchError(this.handleError));
  }

  // Get pep available persons
  getPepAvailable(applicationId: number): Observable<AvailablePepResponse> {
    return this._http.get<AvailablePepResponse>(`${this.baseUrl}/${applicationId}/pep-declarations/available-persons`)
      .pipe(catchError(this.handleError));
  }

  //Get pep persons
  getPepDeclarations(applicationId: number): Observable<PepListResponse> {
    return this._http.get<PepListResponse>(`${this.baseUrl}/${applicationId}/pep-declarations`)
      .pipe(catchError(this.handleError));
  }


  // Get pep person by ID
  getPepDeclarationById(applicationId: number, pepId: number): Observable<PepGetResponse> {
    return this._http.get<PepGetResponse>(`${this.baseUrl}/${applicationId}/pep-declarations/${pepId}`)
      .pipe(catchError(this.handleError));
  }

  // Save PEP Declaration
  savePepDeclaration(applicationId: number, pepDeclaration: CreatePepRequest): Observable<PepGetResponse> {
    return this._http.post<PepGetResponse>(`${this.baseUrl}/${applicationId}/pep-declarations`, pepDeclaration)
      .pipe(catchError(this.handleError));
  }

  //Update PEP Declaration
  updatePepDeclaration(applicationId: number, pepId: number, pepDeclaration: CreatePepRequest): Observable<PepGetResponse> {
    return this._http.put<PepGetResponse>(`${this.baseUrl}/${applicationId}/pep-declarations/${pepId}`, pepDeclaration)
      .pipe(catchError(this.handleError));
  }

  // Delete PEP Declaration
  deletePepDeclaration(applicationId: number, pepId: number): Observable<any> {
    return this._http.delete<any>(`${this.baseUrl}/${applicationId}/pep-declarations/${pepId}`)
      .pipe(catchError(this.handleError));
  }

  // Get Documents
  getDocuments(applicationId: number): Observable<any> {
    return this._http.get<any>(`${this.baseUrl}/${applicationId}/documents/required`)
      .pipe(
        map(response => this.mapResponseToEntityDocuments(response)),
        catchError(this.handleError)
      );
  }

  // upload Document
  uploadDocument(applicationId: number, formData: any): Observable<any> {
    return this._http.post<any>(`${this.baseUrl}/${applicationId}/documents/upload`, formData)
      .pipe(catchError(this.handleError));
  }

  // Upload Skip Document
  skipDocument(applicationId: number, formData: any): Observable<any> {
    return this._http.post<any>(`${this.baseUrl}/${applicationId}/documents/skip`, formData)
      .pipe(catchError(this.handleError));
  }

  /** Clears a saved skip reason on the server (removes skip state without deleting an uploaded file). */
  clearDocumentSkip(
    applicationId: number,
    documentDefinitionId: number,
    personId?: number | null
  ): Observable<any> {
    let url = `${this.baseUrl}/${applicationId}/documents/skip?documentDefinitionId=${documentDefinitionId}`;
    if (personId != null && personId > 0) {
      url += `&personId=${personId}`;
    }
    return this._http.delete<any>(url).pipe(catchError(this.handleError));
  }

  // Download Document
  downloadDocument(applicationId: number, documentDefinitionId: number, personId?: number): Observable<Blob> {
    const personIdParam = personId ? `?personId=${personId}` : '';
    return this._http.get(`${this.baseUrl}/${applicationId}/documents/${documentDefinitionId}/download${personIdParam}`, {
      responseType: 'blob'
    }).pipe(catchError(this.handleError));
  }

  // View Document (opens in new tab)
  viewDocument(applicationId: number, documentDefinitionId: number, personId?: number): Observable<Blob> {
    const personIdParam = personId ? `?personId=${personId}` : '';
    return this._http.get(`${this.baseUrl}/${applicationId}/documents/${documentDefinitionId}/view${personIdParam}`, {
      responseType: 'blob'
    }).pipe(catchError(this.handleError));
  }

  // Delete Document
  deleteDocument(applicationId: number, documentDefinitionId: number, personId?: number | null): Observable<any> {
    const personIdParam = personId ? `?personId=${personId}` : '';
    return this._http.delete<any>(`${this.baseUrl}/${applicationId}/documents/${documentDefinitionId}${personIdParam}`)
      .pipe(catchError(this.handleError));
  }

    // Get unsigned documents list
    getUnsignedDocuments(applicationId: number): Observable<any> {
      return this._http
        .get<any>(`${this.baseUrl}/${applicationId}/document/unsigned-documents`)
        .pipe(catchError(this.handleError));
    }
  
    // Get unsigned document by signedDocumentId
    getUnsignedDocument(applicationId: number, signedDocumentId: number): Observable<Blob> {
      return this._http
        .get(`${this.baseUrl}/${applicationId}/document/unsigned-documents/${signedDocumentId}`, { responseType: 'blob' })
        .pipe(catchError(this.handleError));
    }
  
    // Upload signed PDF (multipart/form-data, field: file)
    uploadSignedDocument(applicationId: number, signedDocumentId: number, file: File): Observable<any> {
      const formData = new FormData();
      formData.append('file', file);
      return this._http
        .post<any>(`${this.baseUrl}/${applicationId}/workflow/upload-signed-document/${signedDocumentId}`, formData)
        .pipe(catchError(this.handleError));
    }

  // Get Controlling Person Employment Details
  getControllingPersonEmploymentDetails(applicationId: number, controllingPersonId: number): Observable<ControllingPersonEmploymentDetailsResponse> {
    return this._http.get<ControllingPersonEmploymentDetailsResponse>(`${this.baseUrl}/${applicationId}/controlling-persons/${controllingPersonId}/employment-details`)
      .pipe(catchError(this.handleError));
  }

  // Save/Update Controlling Person Employment Details
  saveControllingPersonEmploymentDetails(applicationId: number, controllingPersonId: number, employmentData: ControllingPersonEmploymentDetailsRequest): Observable<ControllingPersonEmploymentDetailsResponse> {
    return this._http.post<ControllingPersonEmploymentDetailsResponse>(`${this.baseUrl}/${applicationId}/controlling-persons/${controllingPersonId}/employment-details`, employmentData)
      .pipe(catchError(this.handleError));
  }

  // Review Application
  reviewApplication(applicationId: number, entityName: string): Observable<any> {
    return this._http.get<any>(`${this.baseUrl}/${applicationId}/review/by-entity?entityName=${entityName}`)
      .pipe(catchError(this.handleError));
  }


  // Get Review
  getReviewByEntity(applicationId: number, entityName: string): Observable<any> {
    return this._http.get<any>(`${this.baseUrl}/${applicationId}/review/by-entity?entityName=${entityName}`)
      .pipe(catchError(this.handleError));
  }

  // Get Review History
  getReviewHistoryByEntity(applicationId: number, entityName: string): Observable<any> {
    return this._http.get<any>(`${this.baseUrl}/${applicationId}/review/history-by-entity?entityName=${entityName}`)
      .pipe(catchError(this.handleError));
  }
 
  // Get Legal Entity KYC Persons
  getLegalEntityKycPersons(applicationId: number): Observable<LegalEntityKycPersonsResponse> {
    return this._http.get<LegalEntityKycPersonsResponse>(`${this.baseUrl}/${applicationId}/legal-entity/persons`)
      .pipe(catchError(this.handleError));
  }


  // Common error handler
  private handleError(error: any) {
    console.error('API Error:', error);
    return throwError(() => error);
  }

  private mapResponseToEntityDocuments(response: any): any {
    console.log('Raw response:', response); // Debug log to inspect the structure of the response

    // Access the data property which contains entityDocuments and personDocuments
    const data = response.data || response;

    return {
      // Check if data.entityDocuments is an array, otherwise use an empty array
      entityDocuments: Array.isArray(data.entityDocuments)
        ? data.entityDocuments.map((doc: any) => this.mapDocumentRow(doc))
        : [],

      // Keep additional documents so UI can render "Other Documents Uploads"
      additionalDocuments: Array.isArray(data.additionalDocuments)
        ? data.additionalDocuments.map((doc: any) => this.mapDocumentRow(doc))
        : [],

      // Check if data.personDocuments is an array, otherwise use an empty array
      personDocuments: Array.isArray(data.personDocuments)
        ? data.personDocuments.map((person: any) => this.mapPersonDocument(person))
        : []
    };
  }


  // Maps a single document to the DocumentRow interface
  private mapDocumentRow(doc: any): DocumentRow {
    // If documentName exists in response, it means document is uploaded
    const hasDocumentName = doc.documentName && doc.documentName.trim().length > 0;

    return {
      id: doc.id,
      documentDefinitionId: doc.documentDefinitionId,
      code: doc.code,
      displayName: doc.displayName,
      description: doc.description,
      factaInfo: doc.factaInfo,
      isMandatory: doc.isMandatory,
      isUploaded: hasDocumentName || doc.isUploaded,
      isSkipped: doc.isSkipped,
      isMaster: doc.isMaster,
      documentName: doc.documentName,
      legalEntityId: doc.legalEntityId,
      personId: doc.personId,
      file: doc.file,
      previewUrl: doc.previewUrl,
      fileName: hasDocumentName ? doc.documentName : doc.fileName,
      reasonOfSkip: doc.reasonOfSkip
    };

  }



  // Maps person document data
  private mapPersonDocument(person: any): PersonDocument {
    return {
      id: person.id,
      personId: person.personId,
      fullName: person.fullName,
      roles: person.roles,
      documents: person.documents.map((doc: any) => ({
        ...this.mapDocumentRow(doc),
        personId: person.personId
      }))
    };
  }

  // Fetch and cache (optional prefetch)
  fetchCountries(): void {
    if (!this.countriesCache) {
      this._http
        .get<lookupResponse>(`${environment.baseUrl}/identity/api/Lookup/onboarding/countries`)
        .subscribe({
          next: (data) => {
            this.countriesCache = data;
          },
          error: this.handleError
        });
    }
  }

  getCompletenessStatus(applicationId: number): Observable<any> {
    return this._http.get<any>(`${this.baseUrl}/${applicationId}/validator/entity-completeness-statuses`)
      .pipe(catchError(this.handleError));
  }

  // Observable for components
  getCountries(): Observable<lookupResponse> {
    if (this.countriesCache) {
      return of(this.countriesCache);
    }

    return this._http
      .get<lookupResponse>(`${environment.baseUrl}/identity/api/Lookup/onboarding/countries`)
      .pipe(
        tap(data => (this.countriesCache = data)),
        catchError(this.handleError)
      );
  }


}
